// src/components/AuthModal.js
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createCheckoutSession } from '../services/stripe.service';
import { supabase } from '../utils/supabase';
import { getTierById } from '../config/pricing';
import {
  Mail, Lock, User, Eye, EyeOff, Loader2, Shield, Check, X,
  AlertCircle, Sparkles, ArrowRight, Crown, Clock
} from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';
import { toast } from './Toast';

// Google Icon SVG
const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
    <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

// Apple Icon SVG — fill follows the button's currentColor so it adapts to theme
const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.03 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.56-1.702" />
  </svg>
);

// Discord Icon SVG
const DiscordIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#5865F2" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

const AuthModal = ({ isOpen, onClose, initialMode = 'login', proUpgrade = false, selectedTier = null, selectedInterval = 'month' }) => {
  const {
    signIn,
    signUp,
    signInWithGoogle,
    signInWithApple,
    signInWithDiscord,
    resetPassword,
    mfaRequired,
    verifyMfa,
    signOut,
    currentUser,
    setRedirectingToCheckout
  } = useAuth();

  // Determine if user wants a paid plan (backward compat: proUpgrade OR selectedTier)
  const wantsPaid = !!(selectedTier && selectedTier !== 'free') || proUpgrade;
  const tierInfo = selectedTier ? getTierById(selectedTier) : null;
  const tierPrice = tierInfo ? (selectedInterval === 'year' ? tierInfo.yearlyPrice : tierInfo.monthlyPrice) : 15;
  const tierName = tierInfo ? tierInfo.name : 'Pro';
  const [wantsPro, setWantsPro] = useState(wantsPaid);

  const [mode, setMode] = useState(mfaRequired ? 'mfa' : initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [formError, setFormError] = useState('');
  const [mfaAttempts, setMfaAttempts] = useState(0);
  const [mfaLockoutUntil, setMfaLockoutUntil] = useState(null);

  // MFA security constants
  const MAX_MFA_ATTEMPTS = 5;
  const MFA_LOCKOUT_DURATION = 5 * 60 * 1000; // 5 minutes in ms
  const MFA_SESSION_TIMEOUT = 2 * 60 * 1000; // 2 minutes to complete MFA
  const [mfaTimeRemaining, setMfaTimeRemaining] = useState(null);
  const mfaTimeoutRef = useRef(null);
  const mfaIntervalRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const resendIntervalRef = useRef(null);
  const turnstileRef = useRef(null);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  // Password requirement checks
  const passwordChecks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };
  const allPasswordChecksPassed = Object.values(passwordChecks).every(Boolean);

  useEffect(() => {
    if (isOpen && !mfaRequired) {
      setMode(initialMode);
      setWantsPro(wantsPaid);
    }
  }, [initialMode, isOpen, mfaRequired, wantsPaid]);

  useEffect(() => {
    if (mfaRequired) {
      setMode('mfa');
    } else if (mode === 'mfa') {
      setMode('login');
    }
  }, [mfaRequired]);

  // MFA session timeout - auto-cancel after 5 minutes
  useEffect(() => {
    if (mode === 'mfa') {
      // Set initial time remaining
      setMfaTimeRemaining(MFA_SESSION_TIMEOUT);

      // Start countdown interval
      mfaIntervalRef.current = setInterval(() => {
        setMfaTimeRemaining(prev => {
          if (prev <= 1000) {
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);

      // Set timeout to auto-cancel MFA
      mfaTimeoutRef.current = setTimeout(async () => {
        await signOut();
        setMode('login');
        resetForm();
        setFormError('MFA session timed out. Please sign in again.');
        toast.warning('Session timed out. Please sign in again.');
      }, MFA_SESSION_TIMEOUT);

      return () => {
        // Cleanup on unmount or mode change
        if (mfaTimeoutRef.current) {
          clearTimeout(mfaTimeoutRef.current);
        }
        if (mfaIntervalRef.current) {
          clearInterval(mfaIntervalRef.current);
        }
        setMfaTimeRemaining(null);
      };
    }
  }, [mode]);

  useEffect(() => {
    if (!isOpen && !mfaRequired) {
      // Only reset form when closing if we're NOT in MFA flow
      // This preserves MFA state when user closes and reopens modal
      resetForm();
    }
  }, [isOpen, mfaRequired]);

  // Cleanup intervals on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (resendIntervalRef.current) clearInterval(resendIntervalRef.current);
      if (mfaIntervalRef.current) clearInterval(mfaIntervalRef.current);
      if (mfaTimeoutRef.current) clearTimeout(mfaTimeoutRef.current);
    };
  }, []);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setDisplayName('');
    setShowPassword(false);
    setMfaCode('');
    setMfaError('');
    setFormError('');
    setCaptchaToken('');
    setAgreedToTerms(false);
    setResetEmailSent(false);
    setSignupSuccess(false);
    setResendCooldown(0);
    if (resendIntervalRef.current) {
      clearInterval(resendIntervalRef.current);
    }
    // Note: Don't reset mfaAttempts/mfaLockoutUntil here to maintain lockout across form resets
    if (turnstileRef.current) {
      turnstileRef.current.reset();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormError('');

    if (turnstileSiteKey && !captchaToken) {
      setFormError('Please complete the CAPTCHA verification');
      setLoading(false);
      return;
    }

    // Grab the current token and immediately reset Turnstile for a fresh token
    // This prevents "timeout-or-duplicate" errors on retry
    const currentCaptchaToken = captchaToken;

    try {
      if (mode === 'login') {
        const result = await signIn(email, password, { captchaToken: currentCaptchaToken });
        // Reset Turnstile after use (token is single-use)
        setCaptchaToken('');
        turnstileRef.current?.reset();
        if (result.error) {
          setFormError(result.error.message || 'Failed to sign in');
          return;
        }
        if (result.mfaRequired) {
          setMode('mfa');
        } else {
          onClose();
        }
      } else if (mode === 'signup') {
        if (!allPasswordChecksPassed) {
          setFormError('Please meet all password requirements');
          setLoading(false);
          return;
        }
        if (password !== confirmPassword) {
          setFormError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (!agreedToTerms) {
          setFormError('Please agree to the Terms of Service and Privacy Policy');
          setLoading(false);
          return;
        }
        const result = await signUp(email, password, { full_name: displayName, captchaToken: currentCaptchaToken });
        // Reset Turnstile after use (token is single-use)
        setCaptchaToken('');
        turnstileRef.current?.reset();
        if (result.error) {
          setFormError(result.error.message || 'Failed to sign up');
          return;
        }

        // If user wanted a paid plan, redirect to Stripe checkout
        const newUser = result.data?.user;
        if (wantsPro && newUser) {
          // Set global redirect state to show full-screen loading (prevents dashboard flash)
          setRedirectingToCheckout(true);
          try {
            // Wait for the session to be fully established after signup
            // supabase.functions.invoke needs a valid JWT which may not be ready immediately
            let retries = 0;
            while (retries < 10) {
              const { data: sessionData } = await supabase.auth.getSession();
              if (sessionData?.session?.access_token) break;
              await new Promise(r => setTimeout(r, 500));
              retries++;
            }

            const checkoutResult = await createCheckoutSession(
              newUser.id,
              newUser.email,
              selectedTier || 'pro',
              selectedInterval || 'month'
            );
            if (checkoutResult.error) {
              setRedirectingToCheckout(false);
              toast.error('Account created! ' + checkoutResult.error);
              onClose();
              return;
            }
            // Payment provider will redirect, keep showing the redirect screen
            return;
          } catch (err) {
            console.error('Checkout error:', err);
            setRedirectingToCheckout(false);
            toast.success(`Account created! You can upgrade to ${tierName} from your account settings.`);
            onClose();
            return;
          }
        }

        // Show signup success screen instead of closing
        setSignupSuccess(true);
      } else if (mode === 'reset') {
        const result = await resetPassword(email, { captchaToken: currentCaptchaToken });
        // Reset Turnstile after use (token is single-use)
        setCaptchaToken('');
        turnstileRef.current?.reset();
        if (result.error) {
          setFormError(result.error.message || 'Failed to send reset email');
          return;
        }
        // Show success message and start 2-minute cooldown timer
        setResetEmailSent(true);
        setResendCooldown(120); // 2 minutes in seconds

        // Clear any existing interval
        if (resendIntervalRef.current) {
          clearInterval(resendIntervalRef.current);
        }

        // Start countdown
        resendIntervalRef.current = setInterval(() => {
          setResendCooldown(prev => {
            if (prev <= 1) {
              clearInterval(resendIntervalRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        // Turnstile already reset above after use
      }
    } catch (error) {
      console.error('Auth error:', error);
      setFormError(error.message || 'An unexpected error occurred');
      // Ensure Turnstile gets a fresh token for retry
      setCaptchaToken('');
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();

    // Check if user is locked out
    if (mfaLockoutUntil && Date.now() < mfaLockoutUntil) {
      const remainingSeconds = Math.ceil((mfaLockoutUntil - Date.now()) / 1000);
      const minutes = Math.floor(remainingSeconds / 60);
      const seconds = remainingSeconds % 60;
      setMfaError(`Too many failed attempts. Try again in ${minutes}:${seconds.toString().padStart(2, '0')}`);
      return;
    }

    // Clear lockout if expired
    if (mfaLockoutUntil && Date.now() >= mfaLockoutUntil) {
      setMfaLockoutUntil(null);
      setMfaAttempts(0);
    }

    if (mfaCode.length !== 6) {
      setMfaError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setMfaError('');

    const result = await verifyMfa(mfaCode);

    if (result.error) {
      const newAttempts = mfaAttempts + 1;
      setMfaAttempts(newAttempts);

      if (newAttempts >= MAX_MFA_ATTEMPTS) {
        setMfaLockoutUntil(Date.now() + MFA_LOCKOUT_DURATION);
        setMfaError(`Too many failed attempts. Account locked for 5 minutes.`);
      } else {
        const remaining = MAX_MFA_ATTEMPTS - newAttempts;
        setMfaError(`Invalid code. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining.`);
      }
    } else {
      // Reset attempts on success
      setMfaAttempts(0);
      setMfaLockoutUntil(null);
      onClose();
    }

    setLoading(false);
  };

  const handleCancelMfa = async () => {
    await signOut();
    setMode('login');
    resetForm();
    toast.info('Session canceled.');
  };

  const handleGoogleSignIn = async () => {
    setOauthLoading('google');
    try {
      await signInWithGoogle();
    } finally {
      setOauthLoading('');
    }
  };

  const handleDiscordSignIn = async () => {
    setOauthLoading('discord');
    try {
      await signInWithDiscord();
    } finally {
      setOauthLoading('');
    }
  };

  const handleAppleSignIn = async () => {
    setOauthLoading('apple');
    try {
      await signInWithApple();
    } finally {
      setOauthLoading('');
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    resetForm();
  };

  if (!isOpen) return null;

  // MFA Challenge Screen
  if (mode === 'mfa') {
    return (
      <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-sm bg-theme-card border border-theme rounded-2xl shadow-2xl overflow-hidden">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1 rounded-lg text-theme-muted hover:text-theme hover:bg-theme-hover transition-colors z-10"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="p-6">
            <div className="text-center mb-5">
              <div className="mx-auto w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center mb-3">
                <Shield className="w-5 h-5 text-accent" />
              </div>
              <h2 className="text-lg font-semibold text-theme">Two-Factor Authentication</h2>
              <p className="mt-1 text-xs text-theme-muted">Enter the 6-digit code from your app</p>
              {/* Session timeout countdown */}
              {mfaTimeRemaining !== null && (
                <div className={`mt-2 flex items-center justify-center gap-1.5 text-xs ${
                  mfaTimeRemaining <= 60000
                    ? 'text-red-400'
                    : 'text-theme-muted'
                }`}>
                  <Clock className="h-3.5 w-3.5" />
                  <span>
                    Session expires in {Math.floor(mfaTimeRemaining / 60000)}:{String(Math.floor((mfaTimeRemaining % 60000) / 1000)).padStart(2, '0')}
                  </span>
                </div>
              )}
            </div>

            <form onSubmit={handleMfaSubmit} className="space-y-3">
              <input
                type="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full rounded-lg border border-theme bg-theme-hover px-4 py-2.5 text-center text-xl tracking-[0.4em] font-mono text-theme placeholder:text-theme-muted focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="000000"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
              />
              {mfaError && <p className="text-xs text-red-400 text-center">{mfaError}</p>}
              <button
                type="submit"
                className="w-full py-2.5 rounded-lg font-semibold text-sm bg-accent hover:opacity-90 text-white transition-all disabled:opacity-50"
                disabled={loading || mfaCode.length !== 6}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Verify'}
              </button>
            </form>
            <p className="mt-3 text-center">
              <span onClick={handleCancelMfa} className="text-xs text-theme-muted hover:text-theme cursor-pointer">
                Cancel and sign out
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Signup Success Screen - Check Email
  if (signupSuccess) {
    return (
      <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <div className="relative w-full max-w-sm bg-theme-card border border-theme rounded-2xl shadow-2xl overflow-hidden">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1 rounded-lg text-theme-muted hover:text-theme hover:bg-theme-hover transition-colors z-10"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="p-6">
            <div className="text-center mb-5">
              <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center mb-4">
                <Mail className="w-7 h-7 text-emerald-400" />
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs mb-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                <Sparkles className="w-3 h-3" />
                <span>Free account created!</span>
              </div>
              <h2 className="text-xl font-bold text-theme">Check Your Email</h2>
              <p className="mt-2 text-sm text-theme-muted">
                We've sent a confirmation link to
              </p>
              <p className="mt-1 text-sm font-medium text-theme">
                {email}
              </p>
            </div>

            <div className="space-y-3">
              <div className="p-3 rounded-lg bg-theme-hover border border-theme">
                <p className="text-xs text-theme-muted text-center">
                  Click the link in your email to confirm your account and start using Nexus.
                </p>
              </div>

              <button
                onClick={onClose}
                className="w-full py-2.5 rounded-lg font-semibold text-sm bg-emerald-600 hover:bg-emerald-500 text-theme transition-all flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                Got it!
              </button>
            </div>

            <p className="mt-4 text-center text-xs text-theme-muted">
              Didn't receive the email? Check your spam folder or{' '}
              <span
                onClick={() => { setSignupSuccess(false); setMode('signup'); }}
                className="text-emerald-400 hover:text-emerald-300 cursor-pointer"
              >
                try again
              </span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-md bg-theme-card border border-theme rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1 rounded-lg text-theme-muted hover:text-theme hover:bg-theme-hover transition-colors z-10"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="px-6 pt-6 pb-4 text-center">
          {mode === 'signup' && (
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs mb-3 ${
              wantsPro
                ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400'
                : 'bg-green-500/10 border border-green-500/20 text-green-400'
            }`}>
              {wantsPro ? <Crown className="w-3 h-3" /> : <Sparkles className="w-3 h-3" />}
              <span>{wantsPro ? `${tierName} Plan - $${tierPrice}/${selectedInterval === 'year' ? 'year' : 'month'}` : 'Free Plan'}</span>
            </div>
          )}
          <h2 className="text-xl font-bold text-theme">
            {mode === 'login' && 'Welcome Back'}
            {mode === 'signup' && (wantsPro ? `Create ${tierName} Account` : 'Create Account')}
            {mode === 'reset' && 'Reset Password'}
          </h2>
          <p className="mt-1 text-xs text-theme-muted">
            {mode === 'login' && 'Sign in to continue to Nexus'}
            {mode === 'signup' && (wantsPro ? `Create account, then complete payment` : 'Get started with Nexus for free')}
            {mode === 'reset' && !resetEmailSent && 'Enter your email for a reset link'}
            {mode === 'reset' && resetEmailSent && 'Check your inbox for the reset link'}
          </p>
        </div>

        <div className="px-6 pb-6">
          {/* Password Reset Email Sent Success Banner */}
          {resetEmailSent && mode === 'reset' && (
            <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-green-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-green-400">
                    Reset link sent! Check your inbox and spam folder.
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* OAuth Buttons - Only show for login, not signup */}
          {mode === 'login' && (
            <>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={!!oauthLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-theme bg-theme-hover text-theme text-sm font-medium hover:bg-theme-card transition-colors disabled:opacity-50"
                >
                  {oauthLoading === 'google' ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleIcon />}
                  Google
                </button>
                <button
                  type="button"
                  onClick={handleDiscordSignIn}
                  disabled={!!oauthLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-theme bg-theme-hover text-theme text-sm font-medium hover:bg-theme-card transition-colors disabled:opacity-50"
                >
                  {oauthLoading === 'discord' ? <Loader2 className="h-4 w-4 animate-spin" /> : <DiscordIcon />}
                  Discord
                </button>
                <button
                  type="button"
                  onClick={handleAppleSignIn}
                  disabled={!!oauthLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-theme bg-theme-hover text-theme text-sm font-medium hover:bg-theme-card transition-colors disabled:opacity-50"
                >
                  {oauthLoading === 'apple' ? <Loader2 className="h-4 w-4 animate-spin" /> : <AppleIcon />}
                  Apple
                </button>
              </div>

              <div className="my-4 flex items-center gap-2">
                <div className="h-px flex-1 bg-theme-card" />
                <span className="text-[10px] text-theme-muted uppercase tracking-wide">or with email</span>
                <div className="h-px flex-1 bg-theme-card" />
              </div>
            </>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Display Name (signup only) */}
            {mode === 'signup' && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-muted" />
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full rounded-lg border border-theme bg-theme-hover pl-9 pr-3 py-2 text-sm text-theme placeholder:text-theme-muted focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Your name"
                  required
                />
              </div>
            )}

            {/* Email */}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-muted" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-theme bg-theme-hover pl-9 pr-3 py-2 text-sm text-theme placeholder:text-theme-muted focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="Email address"
                required
              />
            </div>

            {/* Password */}
            {mode !== 'reset' && (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-muted" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-theme bg-theme-hover pl-9 pr-9 py-2 text-sm text-theme placeholder:text-theme-muted focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Password"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-theme-muted hover:text-theme-secondary"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            )}

            {/* Confirm Password (signup only) */}
            {mode === 'signup' && (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-muted" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full rounded-lg border border-theme bg-theme-hover pl-9 pr-3 py-2 text-sm text-theme placeholder:text-theme-muted focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Confirm password"
                  required
                  minLength={8}
                />
              </div>
            )}

            {/* Password Requirements - Compact */}
            {mode === 'signup' && password && (
              <div className="rounded-lg border border-theme bg-theme-hover px-3 py-2">
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                  <span className={`flex items-center gap-1 ${passwordChecks.minLength ? 'text-green-400' : 'text-theme-muted'}`}>
                    {passwordChecks.minLength ? <Check className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-current inline-flex items-center justify-center text-[8px]">8</span>}
                    8+ chars
                  </span>
                  <span className={`flex items-center gap-1 ${passwordChecks.hasUppercase ? 'text-green-400' : 'text-theme-muted'}`}>
                    {passwordChecks.hasUppercase ? <Check className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-current inline-flex items-center justify-center text-[8px]">A</span>}
                    Upper
                  </span>
                  <span className={`flex items-center gap-1 ${passwordChecks.hasLowercase ? 'text-green-400' : 'text-theme-muted'}`}>
                    {passwordChecks.hasLowercase ? <Check className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-current inline-flex items-center justify-center text-[8px]">a</span>}
                    Lower
                  </span>
                  <span className={`flex items-center gap-1 ${passwordChecks.hasNumber ? 'text-green-400' : 'text-theme-muted'}`}>
                    {passwordChecks.hasNumber ? <Check className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-current inline-flex items-center justify-center text-[8px]">1</span>}
                    Number
                  </span>
                  <span className={`flex items-center gap-1 ${passwordChecks.hasSpecial ? 'text-green-400' : 'text-theme-muted'}`}>
                    {passwordChecks.hasSpecial ? <Check className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-current inline-flex items-center justify-center text-[8px]">@</span>}
                    Special
                  </span>
                </div>
              </div>
            )}

            {/* Terms Agreement Checkbox (signup only) */}
            {mode === 'signup' && (
              <label className="flex items-start gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-theme bg-theme-hover text-accent focus:ring-accent focus:ring-offset-0 cursor-pointer"
                />
                <span className="text-xs text-theme-muted leading-relaxed">
                  I agree to the{' '}
                  <Link
                    to="/terms"
                    target="_blank"
                    className="text-accent hover:opacity-80 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link
                    to="/privacy"
                    target="_blank"
                    className="text-accent hover:opacity-80 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </Link>
                </span>
              </label>
            )}

            {/* OAuth linking hint (signup only) */}
            {mode === 'signup' && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-theme-hover border border-theme">
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <GoogleIcon />
                  <DiscordIcon />
                </div>
                <span className="text-[11px] text-theme-muted">
                  You can link Google or Discord after creating your account
                </span>
              </div>
            )}

            {/* Form Error */}
            {formError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-2 text-xs text-red-400">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>{formError}</span>
              </div>
            )}

            {/* Forgot Password Link */}
            {mode === 'login' && (
              <div className="text-right">
                <span
                  onClick={() => switchMode('reset')}
                  className="text-xs text-accent cursor-pointer hover:opacity-80"
                >
                  Forgot password?
                </span>
              </div>
            )}

            {/* Turnstile CAPTCHA — invisible mode: zero footprint, no blank gap */}
            {turnstileSiteKey && (
              <div className="h-0 overflow-hidden" aria-hidden="true">
                <Turnstile
                  key={captchaKey}
                  ref={turnstileRef}
                  siteKey={turnstileSiteKey}
                  onSuccess={(token) => {
                    setCaptchaToken(token);
                    setFormError('');
                  }}
                  onError={() => {
                    setCaptchaToken('');
                    setFormError('CAPTCHA failed. Please try again.');
                    setTimeout(() => setCaptchaKey(k => k + 1), 2000);
                  }}
                  onExpire={() => {
                    setCaptchaToken('');
                    setFormError('CAPTCHA expired. Please verify again.');
                    setCaptchaKey(k => k + 1);
                  }}
                  options={{
                    theme: 'dark',
                    size: 'normal',
                  }}
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className={`w-full py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50 ${
                mode === 'signup' && wantsPro
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-theme'
                  : mode === 'signup'
                  ? 'bg-accent hover:opacity-90 text-white'
                  : 'bg-accent hover:opacity-90 text-white'
              }`}
              disabled={loading || (mode === 'reset' && resendCooldown > 0)}
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === 'login' && 'Sign In'}
              {mode === 'signup' && (
                <>
                  {wantsPro ? <Crown className="w-4 h-4" /> : null}
                  {wantsPro ? 'Continue to Payment' : 'Create Free Account'}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
              {mode === 'reset' && resendCooldown > 0 && (
                <>
                  <Clock className="w-4 h-4" />
                  Resend in {Math.floor(resendCooldown / 60)}:{String(resendCooldown % 60).padStart(2, '0')}
                </>
              )}
              {mode === 'reset' && resendCooldown === 0 && (resetEmailSent ? 'Resend Reset Link' : 'Send Reset Link')}
            </button>
          </form>

          {/* Mode Switcher */}
          <div className="mt-4 text-center text-xs">
            {mode === 'login' && (
              <p className="text-theme-muted">
                Don't have an account?{' '}
                <span onClick={() => switchMode('signup')} className="text-accent cursor-pointer font-medium hover:opacity-80">
                  Sign up free
                </span>
              </p>
            )}
            {mode === 'signup' && (
              <p className="text-theme-muted">
                Already have an account?{' '}
                <span onClick={() => switchMode('login')} className="text-accent cursor-pointer font-medium hover:opacity-80">
                  Sign in
                </span>
              </p>
            )}
            {mode === 'reset' && (
              <p className="text-theme-muted">
                {resetEmailSent ? 'Got the link?' : 'Remember your password?'}{' '}
                <span onClick={() => switchMode('login')} className="text-accent cursor-pointer font-medium hover:opacity-80">
                  Sign in
                </span>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
